import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ModalController } from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { UpdateModalComponent } from '../components/update-modal/update-modal.component';

export type UpdateCheckResult = 'unavailable' | 'up-to-date' | 'update-ready';

/**
 * Actualizaciones de la PWA (Angular Service Worker).
 *
 * El SW descarga la nueva versión en segundo plano; sin esto el usuario
 * necesita dos recargas para verla. Aquí, al detectarse una versión lista
 * (VERSION_READY) se ofrece recargar con un modal centrado que no se
 * descarta con el backdrop, asegurando visibilidad total.
 *
 * SwUpdate.isEnabled es false en dev (localhost) y en apps nativas —
 * ver provideServiceWorker en main.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private swUpdate = inject(SwUpdate);
  private modalController = inject(ModalController);

  // Llamar una sola vez al arranque (AppComponent).
  init() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
      )
      .subscribe(() => this.promptReload());
  }

  /**
   * Chequeo manual. Si encuentra versión nueva, el modal de recarga lo
   * dispara la suscripción de init() (VERSION_READY) — no se duplica aquí.
   */
  async checkForUpdate(): Promise<UpdateCheckResult> {
    if (!this.swUpdate.isEnabled) return 'unavailable';
    const updateFound = await this.swUpdate.checkForUpdate();
    return updateFound ? 'update-ready' : 'up-to-date';
  }

  private async promptReload() {
    const modal = await this.modalController.create({
      component: UpdateModalComponent,
      backdropDismiss: false,
    });
    await modal.present();

    const { role } = await modal.onWillDismiss();
    if (role === 'reload') {
      document.location.reload();
    }
  }
}
